import React from "react";
import { CollageTemplateType } from "../../utils/constants";
import { getAutoLayout } from "../../utils/AutoLayout";

interface Props {
  template: CollageTemplateType;
  images: string[];
  onCellClick?: (index: number) => void;
  onDeleteCell?: (index: number) => void;
  previewMode?: boolean; // new prop
}

const CollageGrid: React.FC<Props> = ({
  template,
  images,
  onCellClick,
  onDeleteCell,
  previewMode = false,
}) => {
  // Determine layout
  const layout =
    template === "auto"
      ? getAutoLayout(images.filter(Boolean).length)
      : (() => {
          switch (template) {
            case "grid1x1":
              return { cols: 1, rows: 1 };
            case "grid1x2":
              return { cols: 2, rows: 1 };
            case "grid2x1":
              return { cols: 1, rows: 2 };
            case "grid2x2":
              return { cols: 2, rows: 2 };
            case "grid3x1":
              return { cols: 3, rows: 1 };
            case "grid3x2":
              return { cols: 3, rows: 2 };
            case "grid3x3":
              return { cols: 3, rows: 3 };
            default:
              return { cols: 3, rows: Math.ceil(images.length / 3) || 2 };
          }
        })();

  const totalCells = Math.max(layout.cols * layout.rows, images.length);

  // Only one image → full page
  if (images.filter(Boolean).length === 1) {
    const img = images.find((i) => i);
    return (
      <div
        className="w-full h-full bg-white overflow-hidden cursor-pointer"
        onClick={() => onCellClick?.(0)}
      >
        {img && (
          <img
            src={img}
            alt="single-full"
            className="w-full h-full object-contain"
            draggable={false}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="grid w-full h-full bg-white"
      style={{
        gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
        gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
        gap: "0px",
      }}
    >
      {Array.from({ length: totalCells }).map((_, i) => {
        const img = images[i];
        return (
          <div
            key={i}
            onClick={() => onCellClick?.(i)}
            className="relative w-full h-full bg-white cursor-pointer"
          >
            {img && (
              <>
                <img
                  src={img}
                  alt={`collage-${i}`}
                  className="absolute inset-0 w-full h-full object-contain"
                  draggable={false}
                />
                {/* Show cross only in preview */}
                {previewMode && onDeleteCell && (
                  <button
                    className="delete-button absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCell(i);
                    }}
                  >
                    ×
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CollageGrid;
